import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../api';
import { useApp } from '../context/AppContext';
import { t, LANGUAGES } from '../i18n/translations';

const s = {
  page: { maxWidth: 680, margin: '0 auto', padding: '32px 16px' },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--heading)', marginBottom: 28 },
  section: { background: 'var(--surface)', borderRadius: 16, padding: 28, marginBottom: 24, boxShadow: 'var(--shadow)' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: 'var(--heading)', marginBottom: 20 },
  label: { display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--text2)', fontSize: 14 },
  input: { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, marginBottom: 14, boxSizing: 'border-box', background: 'var(--surface2)', color: 'var(--text)' },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  success: { background: 'var(--success-bg)', color: 'var(--success-text)', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontWeight: 600 },
  error: { background: 'var(--error-bg)', color: 'var(--error-text)', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontWeight: 600 },
  stripeBtn: { background: '#635bff', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  notice: { color: 'var(--text3)', fontSize: 14, marginBottom: 16 },
  photoWrap: { display: 'flex', alignItems: 'center', gap: 18, marginBottom: 20 },
  photoImg: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--border)' },
  photoInitials: { width: 80, height: 80, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#fff', flexShrink: 0 },
  uploadBtn: { background: 'var(--primary-light)', color: 'var(--primary)', border: '1.5px solid var(--primary-border)', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'inline-block' },
  uploadNote: { color: 'var(--text4)', fontSize: 12, marginTop: 6 },
  removeBtn: { background: 'var(--error-bg)', color: 'var(--error-text)', border: '1.5px solid #fca5a5', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, marginTop: 8, display: 'inline-block' },
  // Appearance
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 },
  rowLabel: { fontWeight: 600, color: 'var(--text2)', fontSize: 15 },
  toggle: { position: 'relative', width: 50, height: 26, cursor: 'pointer' },
  toggleTrack: (on) => ({ width: 50, height: 26, borderRadius: 13, background: on ? 'var(--primary)' : 'var(--border)', transition: 'background 0.2s', position: 'relative', cursor: 'pointer', border: 'none' }),
  toggleThumb: (on) => ({ position: 'absolute', top: 3, left: on ? 26 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }),
  select: { padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

export default function Settings({ user, onProfileUpdate }) {
  const [searchParams] = useSearchParams();
  const { theme, toggleTheme, language, setLanguage } = useApp();
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [photoPreview, setPhotoPreview] = useState(user?.photo || null);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileMsg, setProfileMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeMsg, setStripeMsg] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // 2FA
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [twoFAVerifyCode, setTwoFAVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [twoFAMsg, setTwoFAMsg] = useState(null);

  const T = (key) => t(language, key);

  useEffect(() => {
    api.get('/stripe/status').then(res => setStripeConfigured(res.data.configured)).catch(() => {});
    api.get('/auth/2fa/status').then(res => setTwoFAEnabled(res.data.enabled)).catch(() => {});
    const payment = searchParams.get('payment');
    if (payment === 'success') setStripeMsg({ type: 'success', text: 'Subscription activated successfully!' });
    if (payment === 'cancelled') setStripeMsg({ type: 'error', text: 'Payment was cancelled.' });
  }, []);

  const uploadPhoto = async (file) => {
    setUploadingPhoto(true);
    setProfileMsg(null);
    try {
      const data = new FormData();
      data.append('photo', file);
      const res = await api.post('/upload/photo', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoPreview(res.data.url);
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Photo upload failed' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    try {
      const res = await api.put('/auth/profile', { ...profileForm, photo: photoPreview });
      setProfileMsg({ type: 'success', text: T('profileUpdated') });
      if (onProfileUpdate) onProfileUpdate(res.data.user);
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile' });
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword)
      return setPasswordMsg({ type: 'error', text: T('passwordMismatch') });
    if (passwordForm.newPassword.length < 12)
      return setPasswordMsg({ type: 'error', text: T('passwordShort') });
    try {
      await api.put('/auth/password', { currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword });
      setPasswordMsg({ type: 'success', text: T('passwordChanged') });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password' });
    }
  };

  // ── 2FA handlers ──
  const setup2FA = async () => {
    setTwoFAMsg(null);
    try {
      const res = await api.post('/auth/2fa/setup');
      setQrCode(res.data.qrCode);
      setShowSetup2FA(true);
      setTwoFAVerifyCode('');
    } catch (err) {
      setTwoFAMsg({ type: 'error', text: err.response?.data?.error || 'Failed to set up 2FA' });
    }
  };

  const enable2FA = async (e) => {
    e.preventDefault();
    setTwoFAMsg(null);
    try {
      await api.post('/auth/2fa/enable', { code: twoFAVerifyCode });
      setTwoFAEnabled(true);
      setShowSetup2FA(false);
      setTwoFAMsg({ type: 'success', text: '2FA enabled! Your account is now more secure.' });
    } catch (err) {
      setTwoFAMsg({ type: 'error', text: err.response?.data?.error || 'Invalid code' });
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setTwoFAMsg(null);
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      setTwoFAEnabled(false);
      setShowDisable2FA(false);
      setDisablePassword('');
      setTwoFAMsg({ type: 'success', text: '2FA has been disabled.' });
    } catch (err) {
      setTwoFAMsg({ type: 'error', text: err.response?.data?.error || 'Failed to disable 2FA' });
    }
  };

  const startCheckout = async () => {
    try {
      const res = await api.post('/stripe/checkout');
      window.location.href = res.data.url;
    } catch (err) {
      setStripeMsg({ type: 'error', text: err.response?.data?.error || 'Failed to start checkout' });
    }
  };

  return (
    <div style={s.page} className="page-wrap">
      <div style={s.title} className="big-title">{T('settings')}</div>

      {/* Appearance & Language */}
      <div style={s.section} className="surface">
        <div style={s.sectionTitle}>{T('appearance')}</div>

        <div style={s.row}>
          <span style={s.rowLabel}>{T('darkMode')}</span>
          <button style={s.toggleTrack(theme === 'dark')} onClick={toggleTheme} aria-label="Toggle dark mode">
            <div style={s.toggleThumb(theme === 'dark')} />
          </button>
        </div>

        <div style={s.row}>
          <span style={s.rowLabel}>{T('language')}</span>
          <select style={s.select} value={language} onChange={e => setLanguage(e.target.value)}>
            {LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Profile */}
      <div style={s.section} className="surface">
        <div style={s.sectionTitle}>{T('profile')}</div>
        {profileMsg && <div style={profileMsg.type === 'success' ? s.success : s.error}>{profileMsg.text}</div>}

        {/* Photo */}
        <div style={s.photoWrap}>
          {photoPreview
            ? <img src={`${photoPreview}`} alt="Profile" style={s.photoImg} />
            : <div style={s.photoInitials}>{getInitials(profileForm.name)}</div>
          }
          <div>
            <label style={{ ...s.uploadBtn, cursor: uploadingPhoto ? 'not-allowed' : 'pointer', opacity: uploadingPhoto ? 0.7 : 1 }}>
              {uploadingPhoto ? T('loading') : photoPreview ? T('changePhoto') : T('uploadPhoto')}
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0])}
                disabled={uploadingPhoto} />
            </label>
            <div style={s.uploadNote}>JPG, PNG, GIF up to 2MB</div>
            {photoPreview && (
              <label style={{ ...s.removeBtn, cursor: 'pointer' }} onClick={() => setPhotoPreview(null)}>{T('removePhoto')}</label>
            )}
          </div>
        </div>

        <form onSubmit={saveProfile}>
          <label style={s.label}>{T('fullName')}</label>
          <input style={s.input} value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} required />
          <label style={s.label}>{T('email')}</label>
          <input style={s.input} type="email" value={profileForm.email} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} required />
          <button type="submit" style={s.btn}>{T('saveProfile')}</button>
        </form>
      </div>

      {/* Password */}
      <div style={s.section} className="surface">
        <div style={s.sectionTitle}>{T('changePassword')}</div>
        {passwordMsg && <div style={passwordMsg.type === 'success' ? s.success : s.error}>{passwordMsg.text}</div>}
        <form onSubmit={savePassword}>
          <label style={s.label}>{T('currentPassword')}</label>
          <input style={s.input} type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} required />
          <label style={s.label}>{T('newPassword')}</label>
          <input style={s.input} type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} required />
          <label style={s.label}>{T('confirmPassword')}</label>
          <input style={s.input} type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} required />
          <button type="submit" style={s.btn}>{T('changePassword')}</button>
        </form>
      </div>

      {/* ── Security (2FA) ── */}
      <div style={s.section} className="surface">
        <div style={s.sectionTitle}>🔐 Two-Factor Authentication</div>
        {twoFAMsg && <div style={twoFAMsg.type === 'success' ? s.success : s.error}>{twoFAMsg.text}</div>}

        {twoFAEnabled ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ background: '#16a34a', color: '#fff', borderRadius: 20, padding: '4px 14px', fontWeight: 700, fontSize: 14 }}>✅ 2FA Enabled</span>
            </div>
            <div style={s.notice}>Your account is protected with an authenticator app. Each login requires a 6-digit code.</div>
            {!showDisable2FA ? (
              <button style={{ ...s.btn, background: '#dc2626' }} onClick={() => setShowDisable2FA(true)}>Disable 2FA</button>
            ) : (
              <form onSubmit={disable2FA}>
                <label style={s.label}>Enter your password to confirm</label>
                <input style={s.input} type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} required placeholder="Your current password" />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" style={{ ...s.btn, background: 'var(--border)', color: 'var(--text)', flex: 1 }} onClick={() => { setShowDisable2FA(false); setDisablePassword(''); }}>Cancel</button>
                  <button type="submit" style={{ ...s.btn, background: '#dc2626', flex: 1 }}>Confirm Disable</button>
                </div>
              </form>
            )}
          </div>
        ) : showSetup2FA ? (
          <div>
            <div style={s.notice}>Scan this QR code with <strong>Google Authenticator</strong>, <strong>Authy</strong>, or any TOTP app, then enter the 6-digit code to confirm.</div>
            {qrCode && <img src={qrCode} alt="2FA QR Code" style={{ display: 'block', margin: '16px auto', width: 180, height: 180, borderRadius: 12, border: '2px solid var(--border)' }} />}
            <form onSubmit={enable2FA}>
              <label style={s.label}>Verification Code</label>
              <input style={{ ...s.input, letterSpacing: 8, fontSize: 22, textAlign: 'center' }} type="text" inputMode="numeric" maxLength={6} value={twoFAVerifyCode} onChange={e => setTwoFAVerifyCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" required />
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" style={{ ...s.btn, background: 'var(--border)', color: 'var(--text)', flex: 1 }} onClick={() => setShowSetup2FA(false)}>Cancel</button>
                <button type="submit" style={{ ...s.btn, flex: 1 }} disabled={twoFAVerifyCode.length !== 6}>Enable 2FA</button>
              </div>
            </form>
          </div>
        ) : (
          <div>
            <div style={s.notice}>Add an extra layer of security. When enabled, you'll need a code from your authenticator app each time you log in.</div>
            <button style={s.btn} onClick={setup2FA}>Set Up 2FA</button>
          </div>
        )}
      </div>

      {/* Contact Us */}
      <div style={s.section} className="surface">
        <div style={s.sectionTitle}>Contact Us</div>
        <div style={{ color: 'var(--text2)', fontSize: 15, marginBottom: 16 }}>
          Have a question or need help? Our support team is here for you.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <a href="mailto:myseniorcare.support@gmail.com" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--primary)', fontWeight: 600, fontSize: 15, textDecoration: 'none' }}>
            📧 myseniorcare.support@gmail.com
          </a>
          <a href="https://www.myseniorcare.org" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--primary)', fontWeight: 600, fontSize: 15, textDecoration: 'none' }}>
            🌐 www.myseniorcare.org
          </a>
        </div>
        <div style={{ marginTop: 16 }}>
          <a href="mailto:myseniorcare.support@gmail.com" style={{ ...s.btn, display: 'inline-block', textDecoration: 'none', textAlign: 'center' }}>
            Send us a Message
          </a>
        </div>
      </div>

      {/* Legal */}
      <div style={{ textAlign: 'center', padding: '20px 0 8px', fontSize: 13, color: 'var(--text4)' }}>
        <Link to="/privacy" style={{ color: 'var(--primary)', fontWeight: 600, marginRight: 16 }}>Privacy Policy</Link>
        <Link to="/terms" style={{ color: 'var(--primary)', fontWeight: 600 }}>Terms of Service</Link>
        <div style={{ marginTop: 8 }}>© {new Date().getFullYear()} SeniorCare. All rights reserved.</div>
      </div>

      {/* Stripe */}
      <div style={s.section} className="surface">
        <div style={s.sectionTitle}>{T('subscription')}</div>
        {stripeMsg && <div style={stripeMsg.type === 'success' ? s.success : s.error}>{stripeMsg.text}</div>}

        {user?.isPremium ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ background: '#16a34a', color: '#fff', borderRadius: 20, padding: '4px 14px', fontWeight: 700, fontSize: 14 }}>⭐ Premium Active</span>
            </div>
            <div style={s.notice}>You have full access to all features including unlimited seniors, caregivers, SMS reminders, SOS alerts, and PDF health reports.</div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ background: 'var(--border)', color: 'var(--text2)', borderRadius: 20, padding: '4px 14px', fontWeight: 700, fontSize: 14 }}>Free Plan</span>
            </div>
            <div style={s.notice}>
              <strong>Free Plan Limits:</strong>
              <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 1.8 }}>
                <li>1 Senior, 1 Caregiver</li>
                <li>3 Medications, 5 Appointments</li>
                <li>10 Vitals entries, 5 Journal entries</li>
                <li>No SMS reminders, No SOS, No PDF reports</li>
              </ul>
            </div>
            {stripeConfigured ? (
              <div>
                <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--heading)', marginBottom: 8 }}>Upgrade to Premium — $9.99/month</div>
                <div style={s.notice}>Unlock unlimited everything + SMS reminders + SOS + PDF reports.</div>
                <button style={s.stripeBtn} onClick={startCheckout}>Upgrade Now — $9.99/mo</button>
              </div>
            ) : (
              <div style={s.notice}>Payments not configured yet.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
